import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Info } from "lucide-react";
import { fluxBadgeColors } from "../flux/variants.js";
const TONE_CLASSES = {
    neutral: fluxBadgeColors.zinc,
    info: fluxBadgeColors.sky,
    brand: fluxBadgeColors.amber,
    warning: fluxBadgeColors.amber,
    success: fluxBadgeColors.emerald,
    danger: fluxBadgeColors.red,
};
const DOT_CLASSES = {
    neutral: "bg-zinc-400",
    info: "bg-sky-500",
    brand: "bg-amber-600",
    warning: "bg-amber-500",
    success: "bg-emerald-500",
    danger: "bg-red-500",
};
/** Display config for every ClaimCandidate.status value. */
const CLAIM_STATUS_CONFIG = {
    identified: { label: "Identified", tone: "info" },
    validated: { label: "Validated", tone: "brand" },
    filed: { label: "Filed", tone: "warning" },
    allowed: { label: "Allowed", tone: "success" },
    recovered: { label: "Recovered", tone: "success" },
    rejected: { label: "Rejected", tone: "danger" },
    deferred: { label: "Deferred", tone: "neutral" },
};
/** Display config for every AuthorityStatus.status value. */
const AUTHORITY_STATUS_CONFIG = {
    final: {
        label: "Final",
        tone: "success",
        hint: "No appeal is pending. This ruling stands as decided and can be relied on as-is.",
    },
    upheld: {
        label: "Upheld",
        tone: "success",
        hint: "This ruling was appealed and a higher forum confirmed it. Safe to rely on.",
    },
    pending_appeal: {
        label: "Pending Appeal",
        tone: "warning",
        hint: "This ruling has been appealed and the appeal has not been decided yet. The outcome could still change.",
    },
    stayed: {
        label: "Stayed",
        tone: "danger",
        hint: "A higher forum has paused enforcement of this ruling while the appeal is heard. Do not treat it as settled.",
    },
    reversed: {
        label: "Reversed",
        tone: "danger",
        hint: "A higher forum overturned this ruling. It should not be relied on by itself.",
    },
    superseded: {
        label: "Superseded",
        tone: "neutral",
        hint: "A later ruling or regulation has replaced this one as the applicable authority.",
    },
};
/** Plain-language explanation of what an AuthorityStatus.status value means, for tooltips/captions. */
export function authorityStatusHint(status) {
    return AUTHORITY_STATUS_CONFIG[status]?.hint ?? "";
}
/** Colored pill for rendering a ClaimCandidate.status or AuthorityStatus.status value. */
export function StatusPill({ kind, status, className = "", dot = true, tooltipAlign = "left" }) {
    const config = kind === "claim_candidate"
        ? CLAIM_STATUS_CONFIG[status]
        : AUTHORITY_STATUS_CONFIG[status];
    const label = config?.label ?? status;
    const tone = config?.tone ?? "neutral";
    const hint = kind === "authority_status" ? config?.hint : undefined;
    return (_jsxs("span", { className: ["relative inline-flex", hint ? "group" : "", className].join(" "), children: [_jsxs("span", { className: [
                    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap",
                    hint ? "cursor-help" : "",
                    TONE_CLASSES[tone],
                ].join(" "), children: [dot ? _jsx("span", { className: ["h-1.5 w-1.5 flex-none rounded-full", DOT_CLASSES[tone]].join(" "), "aria-hidden": true }) : null, label, hint ? _jsx(Info, { className: "h-3 w-3 flex-none opacity-70", "aria-hidden": true }) : null] }), hint ? (_jsx("span", { role: "tooltip", className: [
                    "pointer-events-none absolute bottom-full z-50 mb-1.5 w-56 scale-95 rounded-md bg-zinc-900 px-2.5 py-1.5 text-[0.6875rem] font-normal leading-snug text-white opacity-0 shadow-lg transition duration-100 group-hover:scale-100 group-hover:opacity-100",
                    tooltipAlign === "right" ? "right-0" : "left-0",
                ].join(" "), children: hint })) : null] }));
}
/** Generic freeform pill for labels not tied to one of the fixed status enums. */
export function Badge({ tone = "neutral", className = "", dot = false, children, ...rest }) {
    return (_jsxs("span", { className: [
            "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap",
            TONE_CLASSES[tone],
            className,
        ].join(" "), ...rest, children: [dot ? _jsx("span", { className: ["h-1.5 w-1.5 flex-none rounded-full", DOT_CLASSES[tone]].join(" "), "aria-hidden": true }) : null, children] }));
}
